export type AdPlacement = 'left' | 'right';

export type AdSenseConfig = {
  mode: 'adsense';
  clientId: string;
  slots: Record<AdPlacement, string>;
  allowedHosts: string[];
};

export type AdvertisingConfig = { mode: 'off' | 'preview' } | AdSenseConfig;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Config is local, serializable data; it cannot supply executable tags or script URLs. */
export function parseAdvertisingConfig(value: unknown): AdvertisingConfig {
  if (!isRecord(value)) throw new Error('Cấu hình quảng cáo phải là một object.');
  if (value.mode === 'off' || value.mode === 'preview') return { mode: value.mode };
  if (value.mode !== 'adsense') throw new Error('Chế độ quảng cáo không hợp lệ.');
  if (typeof value.clientId !== 'string' || !/^ca-pub-\d{16}$/.test(value.clientId)) {
    throw new Error('Publisher ID AdSense phải có dạng ca-pub- và 16 chữ số.');
  }
  const slots = value.slots;
  if (!isRecord(slots) || !['left', 'right'].every(side =>
    typeof slots[side] === 'string' && /^\d{1,20}$/.test(slots[side] as string))) {
    throw new Error('Cần mã ad slot hợp lệ cho cả banner trái và phải.');
  }
  const hosts = value.allowedHosts;
  if (!Array.isArray(hosts) || !hosts.length || !hosts.every(host =>
    typeof host === 'string' && host.length <= 253 &&
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(host) &&
    !host.endsWith('.localhost'))) {
    throw new Error('Cần danh sách hostname production, không gồm giao thức, đường dẫn hoặc ký tự đại diện.');
  }
  return {
    mode: 'adsense',
    clientId: value.clientId,
    slots: { left: slots.left as string, right: slots.right as string },
    allowedHosts: [...new Set(hosts as string[])],
  };
}

export function canServeAds(config: AdvertisingConfig, location: { protocol: string; hostname: string }): config is AdSenseConfig {
  return config.mode === 'adsense' && location.protocol === 'https:' &&
    config.allowedHosts.includes(location.hostname);
}
